import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { CHECKOUT_ERRORS, STRIPE_PUBLIC_KEY } from '@/constants/constants';
import { usePaywall } from '@/hooks/usePaywall';
import { setPageTitle } from '@/lib/appInsights';
import { PaywallContextType } from '@/types/util.types';
import checkmark from '../assets/checkmark.svg';

const Checkout = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
  const { isLoading, error, purchaseArticle, validateCheckout } = usePaywall() as PaywallContextType;
  const [clientSecret, setClientSecret] = useState('');
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  /*
   * Setup stripe promise with the public key on first load
   */
  useEffect(() => {
    async function fetchStripe() {
      setStripePromise(await loadStripe(STRIPE_PUBLIC_KEY));
    }
    setPageTitle('Checkout');
    fetchStripe();
  }, []);

  /**
   * Get the client secret that creates the checkout session
   */
  useEffect(() => {
    async function validateCheckoutAfterPurchase() {
      validateCheckout(checkoutSessionId as string).then((result) => {
        if (result && result.success) {
          setPurchaseSuccess(true);
          navigate(`/article/${id}`);
        }
      });
      return;
    }

    async function getClientSecret() {
      if (!id || !stripePromise) return;
      const client_secret = await purchaseArticle(parseInt(id));
      if (client_secret) {
        setClientSecret(client_secret);
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const checkoutSessionId = urlParams.get('session_id');

    if (checkoutSessionId) {
      validateCheckoutAfterPurchase();
    } else {
      getClientSecret();
    }
  }, [id, stripePromise, purchaseArticle, validateCheckout, navigate]);

  return (
    <>
      {isLoading ? null : (
        <div className='container mx-auto px-4 sm:px-0 text-center text-[#002327]'>
          {purchaseSuccess && (
            <div className='mt-8 mb-12 text-center'>
              <div className='mt-8 mb-8 text-center'>
                <img src={checkmark} alt='React Logo' className='h-[64px] w-[64px] m-auto' />
              </div>

              <h2 className='font-bold'>Purchase Successful</h2>
              <p>Taking you back to the article...</p>
            </div>
          )}

          {error && (
            <div className='mt-16 mb-12 text-center'>
              <h2 className='font-bold'>{(error.code && CHECKOUT_ERRORS[error.code]?.title) || 'Unknown Error'}</h2>
              <p>
                {(error.code && CHECKOUT_ERRORS[error.code]?.message) ||
                  'An unexpected error occurred. Please try again later or contact our support team for assistance.'}
              </p>

              {/* <div className='max-w-lg mx-auto'>
                <Notification {...error} />
              </div> */}
            </div>
          )}

          {stripePromise && clientSecret && (
            <>
              <div className='mt-8 mb-8 text-center'>
                <h2 className='font-bold'>Purchase Article</h2>
              </div>
              <div className='mt-4 mb-4'>
                <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Checkout;
